import { useAtomValue } from "jotai";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RxMinus, RxPlus } from "react-icons/rx";

import { CardCategoryEnum } from "@sorcery/db";
import { Line, OnClickLink, toast } from "@sorcery/ui";

import { ModalTypeEnum, useModal } from "~/providers/ModalProvider";
import { fullCollectionlistJotai } from "~/state/collectionJotai";
import { deckJotai, decklistJotai, sideboardJotai, trackingJotai } from "~/state/deckJotai";
import type { CardType } from "~/types/card";
import { api } from "~/utils/trpc";

// Queue to manage sequential execution
// TODO move this somewhere so it's reuseable,  but make sure not to break the lodash debouncing on the update
// useMemo will think that each change or item is unique and not put it in queue but make new requests instead
// previous attempts to make this a hook caused the dependencies on the useCallback func to change every run and nullify the debouncing
const requestQueue: (() => Promise<void>)[] = [];
let isProcessing = false;

async function processQueue() {
  if (isProcessing || requestQueue.length === 0) return;

  isProcessing = true;
  const nextRequest = requestQueue.shift();
  if (nextRequest) await nextRequest();
  isProcessing = false;

  // Process the next request
  await processQueue();
}

export const DeckTicker = ({ card }: { card: CardType }) => {
  const { openModal } = useModal();
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const deck = useAtomValue(deckJotai)!;
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const decklist = useAtomValue(decklistJotai)!;
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const sideboard = useAtomValue(sideboardJotai)!;
  const tracking = useAtomValue(trackingJotai);
  const fullCollectionlist = useAtomValue(fullCollectionlistJotai);
  const trpcUtils = api.useUtils();

  const deckCard = decklist.find((dc) => dc.cardId === card.id);
  const sideboardCard = sideboard.find((dc) => dc.cardId === card.id);

  const [localQuantity, setLocalQuantity] = useState(deckCard?.quantity ?? 0);

  // keep in sync with updates from elsewhere (companion decklist etc)
  useEffect(() => {
    setLocalQuantity(deckCard?.quantity ?? 0);
  }, [deckCard?.quantity]);

  const itemsInCollection = useMemo(
    () =>
      fullCollectionlist
        ?.find((c) => c.card.id === card.id)
        ?.groupings.reduce((acc, grouping) => acc + grouping.items.length, 0) ?? 0,
    [fullCollectionlist, card.id],
  );

  const doesntHaveQuantity = useMemo(
    () => tracking && itemsInCollection < localQuantity + (sideboardCard?.quantity ?? 0),
    [itemsInCollection, localQuantity, sideboardCard?.quantity, tracking],
  );

  const { mutateAsync } = api.deck.updateCard.useMutation({
    onSuccess: (deckId) => {
      toast("Deck updated");
      void trpcUtils.deck.getDecklistById.invalidate({ id: deckId });
      void trpcUtils.deck.getHistoryById.invalidate({ id: deckId });
    },
    onError: () => {
      toast.error("Something went wrong updating your deck");
    },
  });

  const queuedMutate = useCallback(
    (data: Parameters<typeof mutateAsync>[0]) =>
      new Promise((resolve, reject) => {
        const executeRequest = async () => {
          try {
            const result = await mutateAsync(data);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        };

        requestQueue.push(executeRequest);
        void processQueue();
      }),
    [mutateAsync],
  );

  const onChange = useCallback(
    (newQuantity: number) => {
      void queuedMutate({
        deckId: deck.id,
        cardId: card.id,
        // biome-ignore lint/style/noNonNullAssertion: <explanation>
        variantId: card.variants[0]!.id,
        decklistQuantity: newQuantity,
        sideboardQuantity: null,
        maybeboardQuantity: null,
      });
    },
    [card.id, card.variants, deck.id, queuedMutate],
  );

  const debouncedChangeHandler = useMemo(() => debounce(onChange, 1000), [onChange]);

  return (
    <>
      <div className="mt-1 flex h-[25px] w-full items-center justify-between">
        {/* dummy for spacing */}
        <div className="w-[25px]" />

        {/* Only render quick select buttons on non avatar cards */}
        {card.guardian?.category === CardCategoryEnum.Avatar ? (
          <OnClickLink
            className="hover:text-primary text-sm transition-colors"
            onClick={() =>
              openModal(ModalTypeEnum.DECKGALLERY, {
                card,
              })
            }
          >
            Options
          </OnClickLink>
        ) : (
          <div
            className={`flex items-center justify-center ${
              // goes red when collection tracker is on and have no quantity available
              tracking && (doesntHaveQuantity ?? itemsInCollection === 0) && "text-red"
            }`}
          >
            {/* less button */}
            {localQuantity === 0 ? (
              <div className="w-[25px]" />
            ) : (
              <div
                onClick={() => {
                  setLocalQuantity(localQuantity - 1);
                  void debouncedChangeHandler(localQuantity - 1);
                }}
                className={
                  "hover:border-primary flex h-[25px] w-[25px] cursor-pointer select-none flex-col items-center justify-center rounded-full p-1 transition-shadow hover:border hover:shadow-[0_0_18px_rgba(255,123,0,0.9)]"
                }
              >
                <RxMinus size={25} />
              </div>
            )}

            {/* current quantity */}
            <div className="mt-1 h-[25px] w-[20px] text-center">{localQuantity}</div>

            {/* more button */}
            <div
              onClick={() => {
                setLocalQuantity(localQuantity + 1);
                void debouncedChangeHandler(localQuantity + 1);
              }}
              className={
                "hover:border-primary flex h-[25px] w-[25px] cursor-pointer select-none flex-col items-center justify-center rounded-full p-1 transition-shadow hover:border hover:shadow-[0_0_18px_rgba(255,123,0,0.9)]"
              }
            >
              <RxPlus size={25} />
            </div>
          </div>
        )}
        {/* dummy for spacing */}
        <div className="flex w-[25px]" />
      </div>
      <Line className="mx-2 mb-1" />
    </>
  );
};
